import { useTranslations } from '@vocab/react';
import { Drawer } from 'braid-design-system';
import { useId, type ComponentProps } from 'react';

import { useConfirm } from 'src/modules/imperative-confirm';
import { DiscardDialog } from 'src/ui/DiscardDialog/DiscardDialog';

import translations from './.vocab';

interface Props extends Omit<ComponentProps<typeof Drawer>, 'onClose' | 'id'> {
  /** used for populating `data-automation` attributes.
   * `${name}-form-drawer`,
   * `${name}-discard-dialog`,
   * `${name}-discard-dialog-button`,
   * `${name}-cancel-dialog-button`
   * */
  name?: string;
  shouldConfirmBeforeClose: boolean;
  onClose: () => void;
}

export function ConfirmableDrawer({
  name = 'generic',
  shouldConfirmBeforeClose,
  onClose,
  ...drawerProps
}: Props) {
  const { t } = useTranslations(translations);
  const [modal, openModal] = useConfirm();
  const handleDrawerClose = async () => {
    const shouldAbort =
      shouldConfirmBeforeClose &&
      (await openModal((close) => (
        <DiscardDialog
          name={name}
          onAbort={() => close(true)}
          onConfirm={() => close(false)}
        />
      )));

    if (shouldAbort) {
      return;
    }

    onClose();
  };

  return (
    <>
      <Drawer
        {...drawerProps}
        id={useId()}
        data={{ ...drawerProps.data, automation: `${name}-form-drawer` }}
        onClose={() => {
          handleDrawerClose();
          // return false to fulfil onClose contract
          // so that Drawer rely solely on open prop
          return false;
        }}
        closeLabel={t('Close')}
      />
      {modal}
    </>
  );
}
